import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Check, KeyRound, Loader2, Lock, Plus, ShieldCheck, Trash2, UserPlus, X,
} from "lucide-react";
import { api, ApiError, type CondominioListItem } from "../lib/api";

interface Vinc { id: number; condominio_id: number; condominio_nome: string; papel: string }
interface Usuario {
  id: number; nome: string; email: string; is_dono: boolean; ativo: boolean; vinculos: Vinc[];
}
interface Meta {
  papeis: { chave: string; rotulo: string }[];
  funcionalidades: { chave: string; rotulo: string }[];
}
interface CelulaPerm { papel: string; funcionalidade: string; pode_ler: boolean; pode_escrever: boolean }

export function UsuariosPage({ onVoltar }: { onVoltar: () => void }) {
  const qc = useQueryClient();
  const [aba, setAba] = useState<"usuarios" | "permissoes">("usuarios");

  const { data: meta } = useQuery<Meta>({ queryKey: ["usuarios-meta"], queryFn: () => api.get("/usuarios/meta") });
  const { data: usuarios } = useQuery<Usuario[]>({ queryKey: ["usuarios"], queryFn: () => api.get("/usuarios") });
  const { data: conds } = useQuery<CondominioListItem[]>({ queryKey: ["condominios"], queryFn: () => api.get("/condominios") });

  return (
    <div>
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-center gap-2 mb-5">
        <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><KeyRound size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usuários e permissões</h1>
          <p className="text-sm text-slate-500">Crie usuários, vincule a condomínios e ative/desative acessos.</p>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {(["usuarios", "permissoes"] as const).map((a) => (
          <button key={a} onClick={() => setAba(a)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${aba === a ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {a === "usuarios" ? "Usuários" : "Permissões (por papel)"}
          </button>
        ))}
      </div>

      {aba === "usuarios"
        ? <AbaUsuarios usuarios={usuarios ?? []} conds={conds ?? []} papeis={meta?.papeis ?? []} qc={qc} />
        : <AbaPermissoes meta={meta} qc={qc} />}
    </div>
  );
}

// ============================ Usuários ============================
function AbaUsuarios({ usuarios, conds, papeis, qc }: {
  usuarios: Usuario[]; conds: CondominioListItem[];
  papeis: { chave: string; rotulo: string }[]; qc: ReturnType<typeof useQueryClient>;
}) {
  const [nome, setNome] = useState(""); const [email, setEmail] = useState(""); const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const inval = () => qc.invalidateQueries({ queryKey: ["usuarios"] });

  const criar = useMutation({
    mutationFn: () => api.post("/usuarios", { nome, email, senha }),
    onSuccess: () => { setNome(""); setEmail(""); setSenha(""); setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Erro ao criar usuário."),
  });
  const excluir = useMutation({ mutationFn: (id: number) => api.delete(`/usuarios/${id}`), onSuccess: inval });
  const vincular = useMutation({
    mutationFn: (v: { uid: number; condominio_id: number; papel: string }) =>
      api.post(`/usuarios/${v.uid}/vinculos`, { condominio_id: v.condominio_id, papel: v.papel }),
    onSuccess: inval,
  });
  const desvincular = useMutation({ mutationFn: (vid: number) => api.delete(`/vinculos/${vid}`), onSuccess: inval });

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><UserPlus size={16} /> Novo usuário</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="Senha" type="text" value={senha} onChange={(e) => setSenha(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <button disabled={!nome || !email || !senha || criar.isPending} onClick={() => criar.mutate()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800 disabled:opacity-50">
            {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Criar
          </button>
        </div>
        {erro && <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}
      </div>

      <div className="space-y-3">
        {usuarios.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {u.nome}
                  {u.is_dono && <span className="text-[10px] font-bold text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5">DONO</span>}
                  {!u.ativo && <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">inativo</span>}
                </div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <RedefinirSenha uid={u.id} nome={u.nome} qc={qc} />
                {!u.is_dono && (
                  <button onClick={() => { if (confirm(`Excluir ${u.nome}?`)) excluir.mutate(u.id); }}
                    className="text-slate-400 hover:text-rose-600" title="Excluir usuário"><Trash2 size={16} /></button>
                )}
              </div>
            </div>

            {!u.is_dono && (
              <div className="mt-3">
                <div className="text-xs font-medium text-slate-500 mb-1">Acesso a condomínios</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {u.vinculos.length === 0 && <span className="text-xs text-slate-400">Nenhum — sem acesso a dados ainda.</span>}
                  {u.vinculos.map((v) => (
                    <span key={v.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5">
                      {v.condominio_nome} · <b>{papeis.find((p) => p.chave === v.papel)?.rotulo ?? v.papel}</b>
                      <button onClick={() => desvincular.mutate(v.id)} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <AddVinculo conds={conds} papeis={papeis} onAdd={(cid, papel) => vincular.mutate({ uid: u.id, condominio_id: cid, papel })} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddVinculo({ conds, papeis, onAdd }: {
  conds: CondominioListItem[]; papeis: { chave: string; rotulo: string }[];
  onAdd: (condominio_id: number, papel: string) => void;
}) {
  const [cid, setCid] = useState<number | "">(""); const [papel, setPapel] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={cid} onChange={(e) => setCid(e.target.value ? Number(e.target.value) : "")}
        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none">
        <option value="">Condomínio…</option>
        {conds.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
      </select>
      <select value={papel} onChange={(e) => setPapel(e.target.value)}
        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none">
        <option value="">Papel…</option>
        {papeis.map((p) => <option key={p.chave} value={p.chave}>{p.rotulo}</option>)}
      </select>
      <button disabled={!cid || !papel} onClick={() => { onAdd(Number(cid), papel); setCid(""); setPapel(""); }}
        className="inline-flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-lg border border-brand-200 text-brand-700 bg-brand-50 hover:brightness-95 disabled:opacity-40">
        <Plus size={14} /> Vincular
      </button>
    </div>
  );
}

// Redefinir a senha de um usuário (o Dono pode trocar a senha de qualquer um,
// inclusive a própria). Reusa PATCH /usuarios/{id} com {senha}. É assim que se
// "reseta" a senha de quem esqueceu: peça ao Dono para definir uma nova aqui.
function RedefinirSenha({ uid, nome, qc }: {
  uid: number; nome: string; qc: ReturnType<typeof useQueryClient>;
}) {
  const [aberto, setAberto] = useState(false);
  const [senha, setSenha] = useState("");
  const [ok, setOk] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const salvar = useMutation({
    mutationFn: () => api.patch(`/usuarios/${uid}`, { senha }),
    onSuccess: () => {
      setOk(true); setSenha(""); setErro(null);
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      setTimeout(() => { setAberto(false); setOk(false); }, 1600);
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível trocar a senha."),
  });

  if (!aberto) {
    return (
      <button onClick={() => { setAberto(true); setOk(false); setErro(null); }}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 border border-slate-200 hover:border-brand-300 rounded-md px-2 py-1"
        title={`Redefinir a senha de ${nome}`}>
        <Lock size={13} /> Redefinir senha
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {ok ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check size={14} /> Senha trocada</span>
      ) : (
        <>
          <input type="text" value={senha} onChange={(e) => setSenha(e.target.value)} autoFocus
            placeholder="Nova senha" className="rounded-md border border-slate-300 px-2 py-1 text-sm w-36 outline-none focus:border-brand-500" />
          <button disabled={!senha || salvar.isPending} onClick={() => salvar.mutate()}
            className="inline-flex items-center gap-1 text-xs bg-brand-700 text-white rounded-md px-2 py-1 hover:bg-brand-800 disabled:opacity-40">
            {salvar.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salvar
          </button>
          <button onClick={() => { setAberto(false); setSenha(""); setErro(null); }}
            className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
        </>
      )}
      {erro && <span className="text-[11px] text-rose-700">{erro}</span>}
    </div>
  );
}

// ============================ Permissões ============================
function AbaPermissoes({ meta, qc }: { meta: Meta | undefined; qc: ReturnType<typeof useQueryClient> }) {
  const { data } = useQuery<{ matriz: CelulaPerm[] }>({ queryKey: ["permissoes"], queryFn: () => api.get("/permissoes") });
  const mapa = useMemo(() => {
    const m = new Map<string, CelulaPerm>();
    (data?.matriz ?? []).forEach((c) => m.set(`${c.papel}|${c.funcionalidade}`, c));
    return m;
  }, [data]);

  const editar = useMutation({
    mutationFn: (c: CelulaPerm) => api.put("/permissoes", c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["permissoes"] }),
  });

  function toggle(papel: string, func: string, campo: "pode_ler" | "pode_escrever") {
    const atual = mapa.get(`${papel}|${func}`) ?? { papel, funcionalidade: func, pode_ler: false, pode_escrever: false };
    editar.mutate({ ...atual, [campo]: !atual[campo] });
  }

  if (!meta || !data) return <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Carregando…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 overflow-x-auto">
      <p className="text-sm text-slate-600 mb-3 flex items-center gap-1.5">
        <ShieldCheck size={16} className="text-brand-600" />
        Marque o que cada <b>papel</b> pode <b>ler</b> (L) e <b>escrever</b> (E) em cada área. O Dono sempre vê tudo.
      </p>
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500">Funcionalidade</th>
            {meta.papeis.map((p) => (
              <th key={p.chave} className="p-2 text-center text-xs font-semibold text-slate-700">{p.rotulo}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {meta.funcionalidades.map((f, i) => (
            <tr key={f.chave} className={i % 2 ? "bg-slate-50/60" : ""}>
              <td className="p-2 text-slate-800">{f.rotulo}</td>
              {meta.papeis.map((p) => {
                const c = mapa.get(`${p.chave}|${f.chave}`);
                return (
                  <td key={p.chave} className="p-2 text-center whitespace-nowrap">
                    <label className="inline-flex items-center gap-1 mr-2" title="Ler">
                      <input type="checkbox" checked={!!c?.pode_ler} onChange={() => toggle(p.chave, f.chave, "pode_ler")} />
                      <span className="text-[11px] text-slate-500">L</span>
                    </label>
                    <label className="inline-flex items-center gap-1" title="Escrever">
                      <input type="checkbox" checked={!!c?.pode_escrever} onChange={() => toggle(p.chave, f.chave, "pode_escrever")} />
                      <span className="text-[11px] text-slate-500">E</span>
                    </label>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-3">As mudanças valem na hora. Casos pontuais (liberar escrita a uma pessoa específica) serão feitos na tela do usuário, em breve.</p>
    </div>
  );
}
